import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  MenuItem,
  Alert,
  Grid,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
} from '@mui/material';
import { ArrowBack, Save, ContentCopy, CheckCircle } from '@mui/icons-material';
import apiClient from '../lib/axios';
import { listPharmacyChains } from '../api/pharmacyChains';

const NAVY = '#0D1B35';
const BLUE = '#0066CC';

const userRoles = [
  { value: 'SUPER_ADMIN', label: 'Super Admin' },
  { value: 'DOCTOR', label: 'Doctor' },
  { value: 'PHARMACY_MANAGER', label: 'Pharmacy Manager' },
  { value: 'PHARMACY_EMPLOYEE', label: 'Pharmacy Employee' },
  { value: 'CONSULTANT', label: 'Consultant' },
];

const baseUserSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.union([z.string().min(8, 'Password must be at least 8 characters'), z.literal('')]),
  role: z.enum(['SUPER_ADMIN', 'DOCTOR', 'PHARMACY_MANAGER', 'PHARMACY_EMPLOYEE', 'CONSULTANT']),
  isActive: z.boolean(),
});

const doctorSchema = baseUserSchema.extend({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  licenseNumber: z.string().optional().or(z.literal('')),
  phoneNumber: z.string().min(10, 'Please enter a valid phone number'),
  specialtyId: z.union([z.string().uuid('Please select a valid specialty'), z.literal('')]),
  clinicName: z.string().optional().or(z.literal('')),
  rncTaxId: z.string().optional().or(z.literal('')),
  clinicAddress: z.string().optional().or(z.literal('')),
});

const pharmacyUserSchema = baseUserSchema.extend({
  pharmacyChainId: z.string().uuid('Selecciona una cadena de farmacia.'),
});

const consultantUserSchema = baseUserSchema.extend({
  fullName: z.string().min(2, 'El nombre es obligatorio'),
  pharmaceuticalLabId: z.union([z.string().uuid(), z.literal('')]).optional(),
});

export const CreateUser: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('DOCTOR');
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [invitationSent, setInvitationSent] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');

  const chainsQuery = useQuery({
    queryKey: ['pharmacy-chains'],
    queryFn: listPharmacyChains,
  });

  const labsQuery = useQuery({
    queryKey: ['pharmaceutical-labs'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ id: string; name: string }[]>('/pharmaceutical-labs');
      return data;
    },
    enabled: selectedRole === 'CONSULTANT',
  });

  const inviteMutation = useMutation({
    mutationFn: async (email: string) => {
      const { data } = await apiClient.post('/admin/users/doctor/invite', { email });
      return data;
    },
    onSuccess: (_, email) => {
      setInvitationSent(true);
      setInviteEmail(email);
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Error al enviar la invitacion');
    },
  });

  const getSchema = () => {
    if (selectedRole === 'DOCTOR') return doctorSchema;
    if (selectedRole === 'PHARMACY_MANAGER' || selectedRole === 'PHARMACY_EMPLOYEE') {
      return pharmacyUserSchema;
    }
    if (selectedRole === 'CONSULTANT') return consultantUserSchema;
    return baseUserSchema;
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<any>({
    resolver: zodResolver(getSchema()),
    defaultValues: {
      email: '',
      password: '',
      role: 'DOCTOR',
      isActive: true,
      firstName: '',
      lastName: '',
      licenseNumber: '',
      phoneNumber: '',
      specialtyId: '',
      isManager: false,
      clinicName: '',
      rncTaxId: '',
      clinicAddress: '',
      pharmacyChainId: '',
      fullName: '',
      pharmaceuticalLabId: '',
    },
  });

  const roleValue = watch('role');
  React.useEffect(() => {
    setSelectedRole(roleValue);
  }, [roleValue]);

  const createUserMutation = useMutation({
    mutationFn: async (data: any) => {
      let endpoint = '/admin/users';
      if (data.role === 'DOCTOR') endpoint = '/admin/users/doctor';
      if (data.role === 'CONSULTANT') endpoint = '/admin/users/consultant';

      const cleanedData = Object.fromEntries(
        Object.entries(data).filter(([_, value]) => value !== '').map(([key, value]) => [key, value])
      );

      const { data: user } = await apiClient.post(endpoint, cleanedData);
      return user;
    },
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] });

      if (user.temporaryPassword) {
        setTemporaryPassword(user.temporaryPassword);
      } else {
        navigate('/admin/users');
      }
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to create user');
    },
  });

  const onSubmit = (data: any) => {
    setError(null);
    createUserMutation.mutate(data);
  };

  const handleCopyPassword = () => {
    if (temporaryPassword) {
      navigator.clipboard.writeText(temporaryPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClosePasswordDialog = () => {
    setTemporaryPassword(null);
    navigate('/admin/users');
  };

  const roleLabel = userRoles.find((r) => r.value === selectedRole)?.label ?? 'Usuario';

  return (
    <Box>
      {/* Hero */}
      <Box
        sx={{
          borderRadius: 4,
          bgcolor: NAVY,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
          p: { xs: '28px 24px', sm: '40px 44px' },
          mb: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', width: 260, height: 260, borderRadius: '50%', bgcolor: 'rgba(0,102,204,0.12)', top: -80, right: -60, pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', width: 140, height: 140, borderRadius: '50%', bgcolor: 'rgba(0,102,204,0.08)', bottom: -50, left: '35%', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', width: 80, height: 80, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.03)', top: '20%', right: '20%', pointerEvents: 'none' }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
          <IconButton size="small" onClick={() => navigate('/admin/users')} sx={{ color: 'rgba(255,255,255,0.5)', p: 0.5 }}>
            <ArrowBack fontSize="small" />
          </IconButton>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase' }}>
            USUARIOS
          </Typography>
        </Box>
        <Typography variant="h2" sx={{ color: '#fff', mb: 0.75 }}>
          Crear Nuevo Usuario
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: { xs: 13, sm: 14 } }}>
          {roleLabel} · Completa los datos para crear la cuenta
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: { xs: 2.5, sm: 4 } }}>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label="User Role"
                    error={!!errors.role}
                    helperText={errors.role?.message as string}
                  >
                    {userRoles.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            {(selectedRole === 'PHARMACY_MANAGER' || selectedRole === 'PHARMACY_EMPLOYEE') && (
              <Grid item xs={12}>
                <Controller
                  name="pharmacyChainId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.pharmacyChainId} required sx={{ mt: 2 }}>
                      <InputLabel id="pharmacy-chain-label">Cadena de Farmacia</InputLabel>
                      <Select
                        {...field}
                        labelId="pharmacy-chain-label"
                        label="Cadena de Farmacia"
                        disabled={chainsQuery.isLoading || chainsQuery.isError}
                      >
                        {chainsQuery.data?.filter((c) => c.isActive).map((chain) => (
                          <MenuItem key={chain.id} value={chain.id}>{chain.name}</MenuItem>
                        ))}
                      </Select>
                      {errors.pharmacyChainId && (
                        <FormHelperText>{errors.pharmacyChainId.message as string}</FormHelperText>
                      )}
                      {chainsQuery.isError && (
                        <FormHelperText>Error al cargar cadenas. Recarga la página.</FormHelperText>
                      )}
                      {!chainsQuery.isLoading && !chainsQuery.isError && chainsQuery.data && chainsQuery.data.filter((c) => c.isActive).length === 0 && (
                        <FormHelperText>
                          No hay cadenas registradas. Crea una en <a href="/admin/pharmacy-chains">Cadenas de Farmacias</a> primero.
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
            )}

            {selectedRole === 'CONSULTANT' && (
              <>
                <Grid item xs={12}>
                  <Controller
                    name="fullName"
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} fullWidth label="Nombre completo"
                        error={!!errors.fullName} helperText={errors.fullName?.message as string} />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="pharmaceuticalLabId"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.pharmaceuticalLabId}>
                        <InputLabel id="lab-label">Laboratorio farmacéutico</InputLabel>
                        <Select
                          {...field}
                          labelId="lab-label"
                          label="Laboratorio farmacéutico"
                          disabled={labsQuery.isLoading || labsQuery.isError}
                        >
                          <MenuItem value="">—</MenuItem>
                          {labsQuery.data?.map((lab) => (
                            <MenuItem key={lab.id} value={lab.id}>{lab.name}</MenuItem>
                          ))}
                        </Select>
                        {errors.pharmaceuticalLabId && (
                          <FormHelperText>{errors.pharmaceuticalLabId.message as string}</FormHelperText>
                        )}
                        {labsQuery.isError && (
                          <FormHelperText>Error al cargar laboratorios. Recarga la página.</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
              </>
            )}

            {selectedRole !== 'DOCTOR' && (
              <>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Email (Login)"
                        type="email"
                        error={!!errors.email}
                        helperText={errors.email?.message as string}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Password (Optional)"
                        type="password"
                        error={!!errors.password}
                        helperText={
                          errors.password?.message as string ||
                          'Leave blank to auto-generate a secure password'
                        }
                      />
                    )}
                  />
                </Grid>
              </>
            )}

            {selectedRole === 'DOCTOR' && (
              <>
                {invitationSent ? (
                  <Grid item xs={12}>
                    <Alert severity="success" sx={{ borderRadius: 2 }}>
                      Invitacion enviada exitosamente a <strong>{inviteEmail}</strong>. El doctor recibira un correo con un enlace para completar su registro.
                    </Alert>
                    <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => { setInvitationSent(false); setInviteEmail(''); }}
                      >
                        Enviar otra invitacion
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => navigate('/admin/users')}
                        sx={{ bgcolor: BLUE, '&:hover': { bgcolor: '#0052a3' } }}
                      >
                        Volver a usuarios
                      </Button>
                    </Box>
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: 13, color: '#657786', mb: 1 }}>
                        Ingresa el correo del doctor. Recibira una invitacion para completar su registro.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Correo del Doctor"
                            type="email"
                            error={!!errors.email}
                            helperText={errors.email?.message as string}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => {
                          const email = watch('email');
                          if (email) inviteMutation.mutate(email);
                        }}
                        disabled={inviteMutation.isPending || !watch('email')}
                        sx={{ bgcolor: BLUE, py: 1.85, '&:hover': { bgcolor: '#0052a3' } }}
                      >
                        {inviteMutation.isPending ? 'Enviando...' : 'Enviar Invitacion'}
                      </Button>
                    </Grid>
                  </>
                )}
              </>
            )}


            <Grid item xs={12}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Active Account"
                  />
                )}
              />
            </Grid>
          </Grid>

          {selectedRole !== 'DOCTOR' && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate('/admin/users')}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<Save />}
                disabled={createUserMutation.isPending}
                sx={{ bgcolor: BLUE, '&:hover': { bgcolor: '#0052a3' } }}
              >
                {createUserMutation.isPending ? 'Creando...' : 'Crear Usuario'}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Temporary Password Dialog */}
      <Dialog open={!!temporaryPassword} onClose={handleClosePasswordDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle color="success" />
            <Typography variant="h6">User Created Successfully</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            The user has been created and a welcome email has been sent with login instructions.
          </Alert>

          <DialogContentText sx={{ mb: 2 }}>
            A temporary password has been auto-generated for this user. Please save this password securely
            as it will only be shown once.
          </DialogContentText>

          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Important Security Notice:
            </Typography>
            <Typography variant="body2">
              The user will be required to change this password on their first login.
            </Typography>
          </Alert>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              bgcolor: 'grey.50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Temporary Password:
              </Typography>
              <Typography variant="h6" fontFamily="monospace" sx={{ letterSpacing: 1, color: 'primary.main' }}>
                {temporaryPassword}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={copied ? <CheckCircle /> : <ContentCopy />}
              onClick={handleCopyPassword}
              color={copied ? 'success' : 'primary'}
            >
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClosePasswordDialog} variant="contained">
            I've Saved the Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
